'use client'

import { usePageData } from '@/contexts/PageDataContext'
import type { VariantProps } from 'cva'
import { cva } from 'cva'
import type { FC, ReactNode } from 'react'
import { useId, useState } from 'react'
import type { Maybe } from '../../frontend-types'
import { Overlay } from '../molecules/Overlay'
import { IconCircle } from './IconCircle'
import { TextAreaContent } from './TextAreaContent'

const cardMasonryVariants = cva({
	base: 'flex flex-col justify-end gap-y-xl p-2xl group rounded-sm text-left',
	variants: {
		theme: {
			'light-blue': 'bg-blue-tint-10 text-blue',
			'light-green': 'bg-green-20 text-blue',
			'dark-blue': 'bg-blue text-white',
		},
		layout: {
			square:
				'h-full col-span-full tablet-p:col-span-4 tablet-p:min-h-84 tablet-l:col-span-6 tablet-l:min-h-87 desktop:col-span-4 desktop:min-h-82.5',
			wide: 'h-auto col-span-full tablet-l:pt-6xl tablet-l:pr-5xl desktop:col-span-8',
		},
		hasDetails: {
			true: 'cursor-pointer',
			false: '',
		},
	},
	defaultVariants: {
		theme: 'light-blue',
		layout: 'square',
		hasDetails: false,
	},
})

type CardMasonryVariants = VariantProps<typeof cardMasonryVariants>

export type CardMasonryType = CardMasonryVariants & {
	id: Maybe<string>
	stats: string
	hasDetails: boolean
	type: 'stat-blocks-masonry'
	details?: ReactNode
}

// Theme to intent mapping for cleaner logic
const THEME_MAP: Record<NonNullable<CardMasonryVariants['theme']>, 'white' | 'blue'> = {
	'light-blue': 'white',
	'light-green': 'white',
	'dark-blue': 'blue',
} as const

type CardMasonryContentProps = {
	cardMasonry: CardMasonryType
}

const CardMasonryContent: FC<CardMasonryContentProps> = ({ cardMasonry }) => {
	const { stats, layout, hasDetails, theme } = cardMasonry
	const iconCircleTheme = theme ? THEME_MAP[theme] : undefined

	return (
		<>
			<TextAreaContent
				text={stats}
				className="font-semibold"
				textSize={layout === 'wide' ? 'text-display' : 'text-body-md'}
			/>
			{hasDetails && <IconCircle icon={['fas', 'plus']} theme={iconCircleTheme} />}
		</>
	)
}

export type CardMasonryProps = {
	cardMasonry: CardMasonryType
}

export const CardMasonry: FC<CardMasonryProps> = ({ cardMasonry }) => {
	const [open, setOpen] = useState<boolean>(false)
	const { theme, layout, details, hasDetails } = cardMasonry

	const { title } = usePageData()

	const labelId = useId()
	const overlayId = useId()

	return (
		<>
			{hasDetails ? (
				<button
					id={labelId}
					type="button"
					className={cardMasonryVariants({ theme, layout, hasDetails })}
					onClick={() => setOpen(true)}
					aria-controls={overlayId}
					aria-expanded={open}
				>
					<CardMasonryContent cardMasonry={cardMasonry} />
				</button>
			) : (
				<div className={cardMasonryVariants({ theme, layout, hasDetails })}>
					<CardMasonryContent cardMasonry={cardMasonry} />
				</div>
			)}
			{hasDetails && (
				<Overlay
					titleTag={title}
					open={open}
					setOpen={setOpen}
					aria-labelledby={labelId}
					id={overlayId}
				>
					{details}
				</Overlay>
			)}
		</>
	)
}
